/* Vehicle collection page (sections/main-collection-build-grid.liquid)
   1. Shows the vehicle named in the URL (?vehicle=&model=&year=), set by the Shop by Vehicle menu
   2. Hero: fits the model name to the width and places the car photo over it
   3. Keeps the part type filter in view while the parts scroll
   4. Part type filter (checkboxes, any number at once; none checked shows every part)
   5. ADRO Select brand filter
   6. Fills the downforce bars when they come into view, counting each value up with its bar
   7. Entrance: the make, model, car and aero row fade in while rising, one after another */
(function () {
  /* Hero layout, top down, on a pure white band: the make sits a fixed distance from the top, the model
     name under it, and the car's highest point a fixed distance below the name. The hero ends a fixed
     distance under the tires, so its height follows the car. Every car is scaled to the same width
     (or the same height, for tall photos), wherever it sits in its photo. The photo itself is shown
     as photographed; only its outer edges fade into the band (see featherMask). */
  var HERO = {
    desktop: { top: 48, gap: 18, nameMax: 138, nameMin: 52, carWidth: 774, carShare: 0.56, carHeight: 297, clearance: 25, ground: 50 },
    phone: { top: 32, gap: 12, nameMax: 72, nameMin: 28, carWidth: 860, carShare: 0.92, carHeight: 330, clearance: 16, ground: 32 }
  };
  var NAME_FILL = 0.88;  /* the name spans up to 88% of the width */
  var WAIT_MAX = 1500;   /* longest wait for the car measurement before the page shows anyway */

  var desktop = window.matchMedia('(min-width: 990px)');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var vehicles = Array.prototype.slice.call(document.querySelectorAll('[data-build-product]'));

  var norm = function (value) { return String(value || '').trim().toUpperCase(); };
  var listOf = function (value) { return String(value || '').split(',').map(norm); };
  var visibleVehicles = function () { return vehicles.filter(function (el) { return !el.hidden; }); };

  /* ---------- 1. Show the vehicle from the URL ---------- */
  function showVehicleFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var vehicle = norm(params.get('vehicle'));
    var model = norm(params.get('model'));
    var year = norm(params.get('year'));
    var showAll = !vehicle || vehicle === 'ALL';
    var matches = 0;

    vehicles.forEach(function (el) {
      var match = showAll || (
        listOf(el.dataset.vehicleFilter).indexOf(vehicle) !== -1 &&
        (!model || listOf(el.dataset.modelFilter).indexOf(model) !== -1) &&
        (!year || listOf(el.dataset.yearFilter).indexOf(year) !== -1)
      );
      el.hidden = !match;
      if (match) matches++;
    });

    /* An old or mistyped link shows every vehicle instead of an empty page */
    if (matches === 0) vehicles.forEach(function (el) { el.hidden = false; });

    document.documentElement.classList.add('vehicle-ready');
  }

  /* ---------- 2. Hero ---------- */

  /* Finds where the car sits in its photo (a cut-out with a transparent background) */
  function measureCar(car) {
    return new Promise(function (resolve) {
      var src = car.getAttribute('src');
      if (!src) { resolve(null); return; }
      var photo = new Image();
      photo.crossOrigin = 'anonymous';
      photo.onload = function () {
        try {
          resolve(findCar(photo, photo.naturalHeight / photo.naturalWidth));
        } catch (error) {
          resolve(null);
        }
      };
      photo.onerror = function () { resolve(null); };
      photo.src = src;
    });
  }

  /* Finds where the car sits, from a small copy of the photo: the first and last solid pixels
     side to side and at the bottom. The roof is the highest solid point across the middle 60% of the car,
     so thin parts (antennas, stray pixels) don't count but a wing does. */
  function findCar(source, ratio) {
    var size = 256;
    var canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    var context = canvas.getContext('2d');
    context.drawImage(source, 0, 0, size, size);
    var data = context.getImageData(0, 0, size, size).data;
    var isCar = function (x, y) {
      var p = (y * size + x) * 4;
      return data[p + 3] > 40 && (data[p] + data[p + 1] + data[p + 2]) / 3 < 215;
    };

    var cols = new Array(size).fill(0);
    var rows = new Array(size).fill(0);
    for (var y = 0; y < size; y++) {
      for (var x = 0; x < size; x++) {
        if (isCar(x, y)) { cols[x]++; rows[y]++; }
      }
    }
    var l = size, r = 0, t = size, b = 0;
    for (x = 0; x < size; x++) if (cols[x] > 2) { l = Math.min(l, x); r = Math.max(r, x); }
    for (y = 0; y < size; y++) if (rows[y] > 4) { t = Math.min(t, y); b = Math.max(b, y); }
    if (r < l || b < t) return null;

    var RUN = 8;
    var tops = [];
    var from = Math.round(l + (r - l) * 0.2);
    var to = Math.round(l + (r - l) * 0.8);
    for (x = from; x <= to; x++) {
      var run = 0;
      for (y = t; y <= b; y++) {
        if (isCar(x, y)) {
          if (++run >= RUN) { tops.push(y - RUN + 1); break; }
        } else {
          run = 0;
        }
      }
    }
    tops.sort(function (m, n) { return m - n; });
    var roof = tops.length ? tops[Math.floor(tops.length * 0.1)] : t;

    return { l: l / size, r: (r + 1) / size, t: t / size, b: (b + 1) / size, roof: roof / size, ratio: ratio };
  }

  /* If the photo can't be measured, treat it as filling its frame with the roof 23% down */
  function fallbackBox(car) {
    var ratio = parseFloat(car.getAttribute('height')) / parseFloat(car.getAttribute('width'));
    return { l: 0, r: 1, t: 0.23, b: 1, roof: 0.23, ratio: ratio || 1 };
  }

  function measureHeroes() {
    return Promise.all(visibleVehicles().map(function (vehicle) {
      var car = vehicle.querySelector('[data-vb-car]');
      if (!car || vehicle.vbCarBox) return null;
      return measureCar(car).then(function (box) {
        vehicle.vbCarBox = box || fallbackBox(car);
      });
    }));
  }

  function placeHero(vehicle) {
    var stage = vehicle.querySelector('[data-vb-stage]');
    if (!stage) return;
    var make = stage.querySelector('.vb__make');
    var name = stage.querySelector('[data-vb-fit]');
    var car = stage.querySelector('[data-vb-car]');
    var width = stage.clientWidth;
    if (!width || !make || !name) return;
    var hero = desktop.matches ? HERO.desktop : HERO.phone;

    stage.classList.add('is-placed');

    /* Model name: as large as fits */
    name.style.fontSize = hero.nameMax + 'px';
    var range = document.createRange();
    range.selectNodeContents(name);
    var textWidth = range.getBoundingClientRect().width || 1;
    var size = Math.max(hero.nameMin, Math.min(hero.nameMax, hero.nameMax * width * NAME_FILL / textWidth));
    name.style.fontSize = size + 'px';

    /* Where the letters actually sit inside the name's line box */
    var nameStyle = window.getComputedStyle(name);
    var context = document.createElement('canvas').getContext('2d');
    context.font = nameStyle.fontWeight + ' ' + size + 'px ' + nameStyle.fontFamily;
    var metrics = context.measureText('E');
    var baselineInBox = (size - (metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent)) / 2 + metrics.fontBoundingBoxAscent;
    var capHeight = metrics.actualBoundingBoxAscent;
    var makeSize = parseFloat(window.getComputedStyle(make).fontSize);

    var baseline = hero.top + makeSize + hero.gap + capHeight;
    make.style.top = hero.top + 'px';
    name.style.top = Math.round(baseline - baselineInBox) + 'px';

    var bottom = baseline - baselineInBox + size;
    if (car) {
      var box = vehicle.vbCarBox || fallbackBox(car);
      var carTop = baseline + hero.clearance; /* the car's highest point, wing included */
      var carWidth = Math.min(hero.carWidth, width * hero.carShare);
      var carHeight = hero.carHeight * carWidth / hero.carWidth;
      var boxWidth = box.r - box.l;
      var imageWidth = carWidth / boxWidth;
      var topToGround = (box.b - box.t) * box.ratio; /* as a share of the photo's width */
      if (imageWidth * topToGround > carHeight) imageWidth = carHeight / topToGround;
      var imageHeight = imageWidth * box.ratio;

      car.style.width = imageWidth + 'px';
      car.style.left = (width / 2 - (box.l + boxWidth / 2) * imageWidth) + 'px';
      car.style.top = (carTop - box.t * imageHeight) + 'px';
      bottom = carTop + (box.b - box.t) * imageHeight;

      if (vehicle.vbCarBox && !vehicle.vbMask) {
        vehicle.vbMask = featherMask(box);
        car.style.webkitMaskImage = vehicle.vbMask;
        car.style.maskImage = vehicle.vbMask;
      }
    }
    stage.style.height = Math.round(bottom + hero.ground) + 'px';
  }

  /* Fades the photo's outer edges into the white band, so a studio backdrop that isn't quite white
     never shows as a box. The car and the area right around it are left alone:
     - top and bottom edges fade across the full width (the car never reaches them)
     - left and right edges fade widely above and below the car, and beside the car only within the
       strip between the photo's edge and the car, stopping short of it */
  function featherMask(box) {
    var size = 256;
    var canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    var context = canvas.getContext('2d');
    var mask = context.createImageData(size, size);
    var ease = function (from, to, value) {
      var t = Math.max(0, Math.min(1, (value - from) / (to - from)));
      return t * t * (3 - 2 * t);
    };
    var carTop = box.t - 0.04;
    var carBottom = box.b + 0.04;
    var nearLeft = Math.max(0.002, box.l - 0.015);
    var nearRight = Math.min(0.998, box.r + 0.015);
    for (var y = 0; y < size; y++) {
      var v = (y + 0.5) / size;
      var topBottom = ease(0, 0.14, v) * ease(1, 0.86, v);
      var besideCar = ease(carTop - 0.06, carTop, v) * ease(carBottom + 0.06, carBottom, v);
      for (var x = 0; x < size; x++) {
        var h = (x + 0.5) / size;
        var wide = ease(0, 0.12, h) * ease(1, 0.88, h);
        var narrow = ease(0, nearLeft, h) * ease(1, nearRight, h);
        var sides = besideCar * narrow + (1 - besideCar) * wide;
        mask.data[(y * size + x) * 4 + 3] = Math.round(topBottom * sides * 255);
      }
    }
    context.putImageData(mask, 0, 0);
    return 'url(' + canvas.toDataURL() + ')';
  }

  function placeHeroes() {
    visibleVehicles().forEach(placeHero);
  }

  /* ---------- 3. Sticky part type filter ----------
     Sits 24px below the header. If the filter is taller than the window,
     it sticks lower so its bottom stays reachable. */
  function headerHeight() {
    var header = document.querySelector('.section-header');
    if (!header) return 0;
    var position = window.getComputedStyle(header).position;
    return (position === 'sticky' || position === 'fixed') ? header.getBoundingClientRect().height : 0;
  }

  function placeFilters() {
    var top = headerHeight() + 24;
    visibleVehicles().forEach(function (el) {
      var filters = el.querySelector('[data-vb-filters]');
      if (!filters) return;
      if (!desktop.matches) { filters.style.top = ''; return; }
      filters.style.top = Math.min(top, window.innerHeight - filters.offsetHeight - 24) + 'px';
    });
  }

  /* ---------- 4. Part type filter ---------- */
  function reveal(items) {
    if (reduceMotion) return;
    items.forEach(function (item, i) {
      item.classList.remove('vb-show');
      void item.offsetWidth;
      item.style.animationDelay = (i * 30) + 'ms';
      item.classList.add('vb-show');
    });
  }

  function filterTypes(vehicle, quiet) {
    var grid = vehicle && vehicle.querySelector('[data-vb-parts]');
    if (!grid) return;
    var on = [];
    Array.prototype.forEach.call(vehicle.querySelectorAll('[data-vb-type]'), function (box) {
      if (box.checked) on.push(box.value);
    });
    var shown = [];
    Array.prototype.forEach.call(grid.querySelectorAll('.product__picker__item'), function (item) {
      var match = !on.length || on.indexOf(item.dataset.ptype || '') !== -1;
      item.classList.toggle('vb-hide', !match);
      if (match) shown.push(item);
    });
    var clear = vehicle.querySelector('[data-vb-clear]');
    if (clear) clear.hidden = !on.length;
    if (!quiet) reveal(shown);
  }

  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-vb-type]')) return;
    filterTypes(event.target.closest('[data-build-product]'));
    /* The page got shorter or longer, so recheck the sticky filter */
    placeFilters();
  });

  /* ---------- 5. ADRO Select brand filter ---------- */
  function setActiveChip(group, chip) {
    Array.prototype.forEach.call(group.querySelectorAll('.vb-chip'), function (c) {
      var on = c === chip;
      c.classList.toggle('is-on', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function filterBrand(select, key) {
    var group = select.querySelector('[data-vb-brand-chips]');
    var chip = group && group.querySelector('[data-vb-key="' + key + '"]');
    if (!chip) return;
    setActiveChip(group, chip);
    select.classList.toggle('is-filtered', key !== 'all');
    var shown = [];
    Array.prototype.forEach.call(select.querySelectorAll('[data-vb-brand]'), function (block) {
      var match = key === 'all' || block.dataset.vbBrand === key;
      block.classList.toggle('vb-hide', !match);
      if (match && key !== 'all' && block.classList.contains('build-grid')) {
        shown = shown.concat(Array.prototype.slice.call(block.querySelectorAll('.product__picker__item')));
      }
    });
    reveal(shown);
  }

  document.addEventListener('click', function (event) {
    var clear = event.target.closest('[data-vb-clear]');
    if (clear) {
      var vehicle = clear.closest('[data-build-product]');
      Array.prototype.forEach.call(vehicle.querySelectorAll('[data-vb-type]'), function (box) { box.checked = false; });
      filterTypes(vehicle);
      placeFilters();
      return;
    }
    var chip = event.target.closest('.vb-chip');
    if (chip && chip.closest('[data-vb-brand-chips]')) {
      filterBrand(chip.closest('[data-vb-select]'), chip.dataset.vbKey);
    }
  });

  /* ---------- 6. Downforce bars ---------- */
  function toMs(value) {
    var first = String(value || '0').split(',')[0].trim();
    return first.indexOf('ms') > -1 ? parseFloat(first) : parseFloat(first) * 1000;
  }

  /* Count each value up from 0 over the same time as its bar's CSS transition */
  function countUp(bars) {
    var fills = bars.querySelectorAll('.vb-bars__fill');
    Array.prototype.forEach.call(bars.querySelectorAll('.vb-bars__live'), function (live, i) {
      var fill = fills[i];
      if (!fill) return;
      var style = window.getComputedStyle(fill);
      var delay = toMs(style.transitionDelay);
      var duration = Math.max(toMs(style.transitionDuration), 1);

      var parts = [];
      var walker = document.createTreeWalker(live, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        var match = node.nodeValue.match(/^([^\d]*?)([\d,]+)([\s\S]*)$/);
        if (match) {
          parts.push({ node: node, before: match[1], target: parseInt(match[2].replace(/,/g, ''), 10), after: match[3] });
        }
      }
      if (!parts.length) return;

      var draw = function (progress) {
        parts.forEach(function (part) {
          part.node.nodeValue = part.before + Math.round(part.target * progress).toLocaleString('en-US') + part.after;
        });
      };

      draw(0);
      var start = null;
      var step = function (now) {
        if (start === null) start = now + delay;
        var t = Math.min(1, Math.max(0, (now - start) / duration));
        draw(1 - Math.pow(1 - t, 4));
        if (t < 1) window.requestAnimationFrame(step);
      };
      window.requestAnimationFrame(step);
    });
  }

  function watchBars() {
    var bars = document.querySelectorAll('[data-vb-bars]');
    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(bars, function (b) { b.classList.add('is-in'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          countUp(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    Array.prototype.forEach.call(bars, function (b) { observer.observe(b); });
  }

  /* ---------- 7. Entrance ----------
     Apple's timing, the same as the homepage sections: each item rises 30px (0.7s) while it fades
     in (0.9s), on easeInOutQuad, 0.15s after the one before. The make, model and car play as soon as
     the page shows. The aero row plays when it is 15% of the screen height into view, which on
     desktop is straight away, so it carries on the same sequence. Skipped with reduced motion. */
  var ENTER_EASE = 'cubic-bezier(0.455, 0.03, 0.515, 0.955)';
  var ENTER_STEP = 150;

  function enter(items, startDelay) {
    items.forEach(function (el, i) {
      if (!el.animate) return;
      var delay = startDelay + i * ENTER_STEP;
      el.animate([{ translate: '0 30px' }, { translate: '0 0' }], { duration: 700, delay: delay, easing: ENTER_EASE, fill: 'backwards' });
      el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 900, delay: delay, easing: ENTER_EASE, fill: 'backwards' });
    });
  }

  function playEntrance() {
    if (reduceMotion) return;
    var start = performance.now();
    visibleVehicles().forEach(function (vehicle) {
      var hero = [
        vehicle.querySelector('.vb__make'),
        vehicle.querySelector('.vb__model'),
        vehicle.querySelector('[data-vb-car]')
      ].filter(Boolean);
      enter(hero, 0);

      var aero = vehicle.querySelector('.vb-aero');
      if (!aero) return;
      var aeroItems = Array.prototype.slice.call(aero.querySelectorAll('.vb-aero__stat, .vb-aero__note'));
      var heroEnd = hero.length * ENTER_STEP;
      if (!('IntersectionObserver' in window)) {
        enter(aeroItems, heroEnd);
        return;
      }
      aero.setAttribute('data-entrance', 'pending');
      var observer = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        observer.disconnect();
        aero.removeAttribute('data-entrance');
        enter(aeroItems, Math.max(0, heroEnd - (performance.now() - start)));
      }, { rootMargin: '0px 0px -15% 0px' });
      observer.observe(aero);
    });
  }

  /* ---------- Start ---------- */
  function layout() {
    placeHeroes();
    placeFilters();
  }

  function init() {
    showVehicleFromUrl();

    /* The shown car's photo loads right away instead of waiting to scroll into view */
    visibleVehicles().forEach(function (vehicle) {
      var car = vehicle.querySelector('[data-vb-car]');
      if (car) car.loading = 'eager';
      /* The browser may restore ticked boxes on Back */
      filterTypes(vehicle, true);
    });

    layout();
    watchBars();

    /* The page stays hidden until the cars are measured and the fonts are in, so nothing jumps.
       It shows after WAIT_MAX at the latest. */
    var ready = Promise.all([measureHeroes(), document.fonts ? document.fonts.ready : null]);
    var timeout = new Promise(function (resolve) { setTimeout(resolve, WAIT_MAX); });
    Promise.race([ready, timeout]).then(function () {
      layout();
      document.documentElement.classList.add('vehicle-placed');
      playEntrance();
    });
    ready.then(layout);

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(layout, 120);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();